/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import utils from '../../../common/utils';
import getAuditExportMessage, { showExportNotification } from '../common/utils/auditExportMessages';

const parseErrorPayloadMessage = async (data) => {
  if (!data) {
    return null;
  }

  if (data instanceof Blob) {
    try {
      const text = await data.text();
      if (!text) {
        return null;
      }

      try {
        const parsed = JSON.parse(text);
        return parsed?.message || parsed?.error?.message || parsed?.error || text;
      } catch {
        return text;
      }
    } catch {
      return null;
    }
  }

  if (typeof data === 'string') {
    return data;
  }

  return data?.message || data?.error?.message || data?.error || null;
};

const isValidExportBlob = (response) => {
  const { data, headers } = response || {};

  if (!(data instanceof Blob)) {
    return false;
  }

  if (!data.size) {
    return false;
  }

  const contentType = headers?.['content-type'] || headers?.['Content-Type'] || data.type || '';

  if (contentType.includes('application/json')) {
    return false;
  }

  return true;
};

const handleAuditExportResponse = async (response, filename) => {
  if (!isValidExportBlob(response)) {
    let message = getAuditExportMessage('aiadmin.EXPORT_EMPTY_RESPONSE');

    if (response?.data instanceof Blob && response.data.type.includes('json')) {
      message = (await parseErrorPayloadMessage(response.data)) || getAuditExportMessage('aiadmin.EXPORT_FAILED');
    }

    showExportNotification(message, 'error');
    return;
  }

  const { data } = response;
  utils.downloadBlobFile({ blob: data, filename });
  showExportNotification(getAuditExportMessage('aiadmin.EXPORT_DOWNLOAD_SUCCESS', { filename }), 'success');
};

const handleAuditExportError = async (error) => {
  const { response } = error;

  if (response) {
    const { status, data } = response;

    if (status === 401) {
      utils.redirectToLogin();
      return;
    }
    const message = (await parseErrorPayloadMessage(data)) || getAuditExportMessage('aiadmin.EXPORT_FAILED');
    showExportNotification(message, 'error');
    return;
  }

  const fallbackMessage =
    error?.message === 'Network Error'
      ? getAuditExportMessage('aiadmin.EXPORT_NETWORK_ERROR')
      : error?.message || getAuditExportMessage('aiadmin.EXPORT_FAILED_RETRY');

  showExportNotification(fallbackMessage, 'error');
};

export { handleAuditExportResponse, handleAuditExportError };
